import { useEffect, useMemo, useState } from "react";
import api from "@/utils/axios";
import VideoUpload from "@/components/video/VideoUpload/VideoUpload";
import styles from "./AdminWelcome.module.scss";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";

const LANGS = ["en", "si", "ta", "hi"];

export default function AdminWelcome() {
  const [activeLang, setActiveLang] = useState("en");
  const [videoId, setVideoId] = useState(null);

  // i18n state
  const [page, setPage] = useState({
    en: { title: "", subtitle: "" },
    si: { title: "", subtitle: "" },
    ta: { title: "", subtitle: "" },
    hi: { title: "", subtitle: "" },
  });

  const makeStep = () => ({
    en: { title: "", description: "" },
    si: { title: "", description: "" },
    ta: { title: "", description: "" },
    hi: { title: "", description: "" },
  });
  const [steps, setSteps] = useState([
    makeStep(),
    makeStep(),
    makeStep(),
    makeStep(),
  ]);

  // UI messages
  const [textMsg, setTextMsg] = useState("");
  const [textErr, setTextErr] = useState("");
  const [videoMsg, setVideoMsg] = useState("");
  const [videoErr, setVideoErr] = useState("");

  // Token for VideoPlayer (proxy mode needs raw token for HLS XHRs)
  const token = useMemo(() => {
    if (typeof window === "undefined") return "";
    // Axios stores the access token here:
    return localStorage.getItem("cm_at") || "";
  }, []);

  // helpers
  function setPageField(lang, field, value) {
    setPage((p) => ({ ...p, [lang]: { ...p[lang], [field]: value } }));
  }
  function setStepField(stepIdx, lang, field, value) {
    setSteps((prev) => {
      const next = [...prev];
      next[stepIdx] = {
        ...next[stepIdx],
        [lang]: { ...next[stepIdx][lang], [field]: value },
      };
      return next;
    });
  }

  // ---- LOAD current doc (like HomeCMS/LanguageCMS)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/v1/welcome/admin"); // { meta, steps, videoId? }
        if (!alive) return;

        // backend stores i18n in "meta"
        const meta = data?.meta || {};
        const loadedPage = {
          en: {
            title: meta.en?.title || "",
            subtitle: meta.en?.subtitle || "",
          },
          si: {
            title: meta.si?.title || "",
            subtitle: meta.si?.subtitle || "",
          },
          ta: {
            title: meta.ta?.title || "",
            subtitle: meta.ta?.subtitle || "",
          },
          hi: {
            title: meta.hi?.title || "",
            subtitle: meta.hi?.subtitle || "",
          },
        };
        setPage(loadedPage);

        const loadedSteps =
          Array.isArray(data?.steps) && data.steps.length === 4
            ? data.steps.map((s) => ({
                en: {
                  title: s?.en?.title || "",
                  description: s?.en?.description || "",
                },
                si: {
                  title: s?.si?.title || "",
                  description: s?.si?.description || "",
                },
                ta: {
                  title: s?.ta?.title || "",
                  description: s?.ta?.description || "",
                },
                hi: {
                  title: s?.hi?.title || "",
                  description: s?.hi?.description || "",
                },
              }))
            : [makeStep(), makeStep(), makeStep(), makeStep()];
        setSteps(loadedSteps);

        // optional video link for preview (string or {_id})
        const vid = data?.videoId?._id || data?.videoId || null;
        setVideoId(vid);
      } catch {
        // keep UI silent on first load unless you want a banner
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // ---- SAVE text
  const saveUrl = useMemo(() => "/v1/welcome/admin/text", []);
  async function handleSaveText() {
    try {
      setTextErr("");
      setTextMsg("Saving…");
      // IMPORTANT: backend expects { meta, steps }
      const payload = { meta: page, steps };
      await api.post(saveUrl, payload); // api has interceptor to add Authorization
      setTextMsg("Saved ✔");
    } catch (e) {
      setTextErr(e?.response?.data?.message || e?.message || "Failed to save");
      setTextMsg("");
    }
  }

  // Optional: show a one-time note after the uploader submits
  function handleUploadNotice() {
    setVideoErr("");
    setVideoMsg(
      "Upload submitted. The Welcome page will auto-publish when processing finishes."
    );
  }

  return (
    <div className={`nu-page ${styles.wrap}`}>
      <h2 className={styles.h2}>Welcome Page — Admin CMS</h2>

      {/* ========================= 4A. TEXT CMS ========================= */}
      <section className={`nu-card ${styles.card}`}>
        <h3 className={styles.h3}>4A. Text CMS (4 steps × i18n)</h3>

        <div className={styles.tabs}>
          {LANGS.map((l) => {
            const active = activeLang === l;
            return (
              <button
                key={l}
                onClick={() => setActiveLang(l)}
                className={`nu-btn nu-btn--sm ${
                  active ? "is-mr" : "nu-btn--ghost"
                } ${styles.tab}`}
                aria-pressed={active ? "true" : "false"}
              >
                {l.toUpperCase()}
              </button>
            );
          })}
        </div>

        {/* Page Title / Subtitle for active language */}
        <div className={styles.group}>
          <div className={styles.groupTitle}>Page</div>
          <div className={styles.inputsGrid}>
            <input
              className={styles.textInput}
              placeholder={`${activeLang.toUpperCase()} Page Title`}
              value={page[activeLang].title}
              onChange={(e) =>
                setPageField(activeLang, "title", e.target.value)
              }
            />
            <input
              className={styles.textInput}
              placeholder={`${activeLang.toUpperCase()} Page Subtitle`}
              value={page[activeLang].subtitle}
              onChange={(e) =>
                setPageField(activeLang, "subtitle", e.target.value)
              }
            />
          </div>
        </div>

        {/* 4 Steps */}
        <div className={styles.stepsWrap}>
          {steps.map((st, idx) => (
            <div key={idx} className={styles.stepCard}>
              <div className={styles.stepHeader}>Step {idx + 1}</div>
              <div className={styles.inputsGrid}>
                <input
                  className={styles.textInput}
                  placeholder={`${activeLang.toUpperCase()} Step ${
                    idx + 1
                  } Title`}
                  value={st[activeLang].title}
                  onChange={(e) =>
                    setStepField(idx, activeLang, "title", e.target.value)
                  }
                />
                <textarea
                  rows={3}
                  className={styles.textarea}
                  placeholder={`${activeLang.toUpperCase()} Step ${
                    idx + 1
                  } Description`}
                  value={st[activeLang].description}
                  onChange={(e) =>
                    setStepField(idx, activeLang, "description", e.target.value)
                  }
                />
              </div>
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          <button onClick={handleSaveText} className="nu-btn is-mr">
            Save Text
          </button>
          {textMsg && <span className={styles.ok}>{textMsg}</span>}
          {textErr && <span className={styles.err}>⚠ {textErr}</span>}
        </div>
      </section>
      {videoId ? (
        <VideoPlayer
          token={token}
          apiBase="https://cmates.onrender.com/api"
          videoId={videoId}
          mode="proxy"
          bleed
          overlayAnimated
          overlayOpacity={0.9}
        />
      ) : (
        <div className={styles.help}>
          No video attached yet. Upload & submit to preview here.
        </div>
      )}
      {/* ========================= 4B. VIDEO CMS ========================= */}
      <section className={`nu-card ${styles.card}`}>
        <h3 className={styles.h3}>4B. Video CMS</h3>
        <p className={styles.help}>
          Upload your Welcome video and click <b>Submit</b>. When processing
          finishes, the server will <b>auto-attach</b> the video and{" "}
          <b>auto-publish</b> the Welcome page.
        </p>

        {/* No onDone/attach; we just show a small note once submit starts */}
        <VideoUpload
          defaultLang="en"
          allowSubtitles
          allowThumbnails
          onSubmitStarted={handleUploadNotice}
          initialVideoId={videoId}
          showUploader={true}
        />

        {(videoMsg || videoErr) && (
          <div className={styles.actions}>
            {videoMsg && <span className={styles.ok}>{videoMsg}</span>}
            {videoErr && <span className={styles.err}>⚠ {videoErr}</span>}
          </div>
        )}
      </section>
    </div>
  );
}
